/**
 * "Einstellungen" - the five config tables of the German return, editable.
 *
 * WHY A REGISTRY AND NOT FIVE COMPONENTS. Roman's requirement was that a further setting later
 * is a line here, not a new component. So this file holds a list of SECTIONS - which columns to
 * show, in what order, with which labels - and everything else is generic: `ConfigTable`
 * renders any of them, `ConfigRowDialog` edits any of them, `ConfigAuditPanel` shows any of
 * their histories. The `api` slot the plan sketched collapsed into `key`: the endpoint is
 * `v1/vat/config/{key}` for every section, so a per-section api function would have been five
 * copies of one line.
 *
 * The FIELD contracts - what may be null, what the allowed values are, how long a column is -
 * are NOT here. They come from the server with the config (`contracts`), which is the same
 * description `config-admin.ts` validates against. A section says how a table LOOKS; the server
 * says what it may CONTAIN.
 *
 * WHY THIS TAB IS ONLY VISIBLE TO AN EDITOR (see VatReportingPage): every row in here decides
 * what gets filed with a tax authority. A read-only view of the rules already exists - it is
 * the derivation ladder in the UStVA tab, which shows exactly which posting groups fed which
 * Kennzahl for the month in front of you.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Info, Loader2, X } from 'lucide-react';
import { vatReportingApi } from '../../services/vatReportingApi';
import { ConfigTable, configRowKey, type ConfigColumnMeta } from './ConfigTable';
import { ConfigRowDialog } from './ConfigRowDialog';
import { ConfigAuditPanel } from './ConfigAuditPanel';
import { useVatLang } from './VatLang';
import type {
    ConfigResource, ConfigResourceContract, ConfigRow, VatCompany, VatConfigResponse,
} from '../../types/vatReporting';

interface Props {
    company: VatCompany;
    country?: string;
    canEdit: boolean;
    onError: (message: string) => void;
}

interface ConfigSection {
    key: ConfigResource;
    /** Columns, in display order. Names must exist in the resource contract. */
    columns: ConfigColumnMeta[];
    /** Field order inside the dialog; the rest follows in contract order. */
    formOrder?: string[];
    /** An amber note above the table, when this table has something a user must know. */
    warningKey?: string;
    /** Rows to draw attention to. */
    rowClass?: (row: ConfigRow) => string | undefined;
}

const DISPOSITION_BADGE: Record<string, string> = {
    mapped: 'bg-green-50 text-green-800 border-green-200',
    not_reported: 'bg-gray-100 text-gray-700 border-gray-200',
    pending: 'bg-amber-100 text-amber-900 border-amber-300',
};

const SECTIONS: ConfigSection[] = [
    {
        // First, and deliberately: this is the table that decides what reaches a Kennzahl, and
        // the one carrying the unanswered combinations. Resolving a `pending` row here is the
        // whole reason this tab exists.
        key: 'line-map',
        columns: [
            { name: 'vatBusPostingGroup', labelKey: 'config.field.vatBusPostingGroup', width: 150 },
            { name: 'vatProdPostingGroup', labelKey: 'config.field.vatProdPostingGroup', width: 150 },
            { name: 'direction', labelKey: 'config.field.direction', width: 90 },
            {
                name: 'disposition', labelKey: 'config.field.disposition', width: 120,
                render: (row) => {
                    const d = String(row.disposition ?? '');
                    return (
                        <span className={`px-1.5 py-0.5 rounded text-xs border ${DISPOSITION_BADGE[d] ?? ''}`}>
                            {d}
                        </span>
                    );
                },
            },
            { name: 'lineRole', labelKey: 'config.field.lineRole', width: 130 },
            { name: 'kennzahl', labelKey: 'config.field.kennzahl', width: 70, align: 'right' },
            { name: 'kennzahlAcqTax', labelKey: 'config.field.kennzahlAcqTax', width: 90, align: 'right' },
            { name: 'taxRatePct', labelKey: 'config.field.taxRatePct', width: 80, align: 'right' },
            { name: 'sign', labelKey: 'config.field.sign', width: 70, align: 'right' },
            { name: 'ecSalesType', labelKey: 'config.field.ecSalesType', width: 80 },
            { name: 'includeInIntrastat', labelKey: 'config.field.includeInIntrastat', width: 90 },
            { name: 'note', labelKey: 'config.field.note', width: 300 },
        ],
        formOrder: [
            'vatBusPostingGroup', 'vatProdPostingGroup', 'direction', 'disposition',
            'lineRole', 'kennzahl', 'kennzahlAcqTax', 'taxRatePct', 'sign', 'ecSalesType',
            'includeInIntrastat', 'note',
        ],
        rowClass: (row) => row.disposition === 'pending' ? 'bg-amber-50 hover:bg-amber-100' : undefined,
    },
    {
        key: 'scope',
        columns: [
            { name: 'vatBusPostingGroup', labelKey: 'config.field.vatBusPostingGroup', width: 200 },
            { name: 'note', labelKey: 'config.field.note', width: 500 },
        ],
        warningKey: 'config.section.scope.warning',
    },
    {
        key: 'country',
        columns: [
            { name: 'taxNumber', labelKey: 'config.field.taxNumber', width: 130 },
            { name: 'ownVatRegistrationNo', labelKey: 'config.field.ownVatRegistrationNo', width: 130 },
            { name: 'taxOffice', labelKey: 'config.field.taxOffice', width: 110 },
            { name: 'standardRatePct', labelKey: 'config.field.standardRatePct', width: 90, align: 'right' },
            { name: 'intrastatNatureOfTransaction', labelKey: 'config.field.intrastatNatureOfTransaction', width: 90 },
            { name: 'intrastatModeOfTransport', labelKey: 'config.field.intrastatModeOfTransport', width: 90 },
            { name: 'intrastatRegionDomestic', labelKey: 'config.field.intrastatRegionDomestic', width: 90 },
            { name: 'intrastatRegionForeign', labelKey: 'config.field.intrastatRegionForeign', width: 90 },
            { name: 'note', labelKey: 'config.field.note', width: 260 },
        ],
        warningKey: 'config.section.country.warning',
    },
    {
        key: 'gl-account',
        columns: [
            { name: 'glAccountNo', labelKey: 'config.field.glAccountNo', width: 120 },
            { name: 'accountRole', labelKey: 'config.field.accountRole', width: 120 },
            { name: 'accountName', labelKey: 'config.field.accountName', width: 260 },
            { name: 'note', labelKey: 'config.field.note', width: 300 },
        ],
    },
    {
        key: 'own-registration',
        columns: [
            { name: 'vatBusPostingGroup', labelKey: 'config.field.vatBusPostingGroup', width: 170 },
            { name: 'countryCode', labelKey: 'config.field.countryCode', width: 80 },
            { name: 'vatRegistrationNo', labelKey: 'config.field.vatRegistrationNo', width: 170 },
            { name: 'note', labelKey: 'config.field.note', width: 400 },
        ],
        formOrder: ['vatBusPostingGroup', 'countryCode', 'vatRegistrationNo', 'note'],
        // This one is not only ours: BILLTO_VATID_MISMATCH in the DocuWare PO check reads it,
        // and its key has no country in it.
        warningKey: 'config.section.own-registration.warning',
    },
];

interface DialogState {
    mode: 'create' | 'edit';
    resource: ConfigResource;
    initial: ConfigRow;
}

export function SettingsTab({ company, country = 'DE', canEdit, onError }: Props) {
    const { t } = useVatLang();
    const [config, setConfig] = useState<VatConfigResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [active, setActive] = useState<ConfigResource>('line-map');
    const [dialog, setDialog] = useState<DialogState | null>(null);
    const [confirmDelete, setConfirmDelete] = useState<{ resource: ConfigResource; row: ConfigRow } | null>(null);
    const [deleting, setDeleting] = useState(false);
    const [notice, setNotice] = useState<string | null>(null);
    const [reloadToken, setReloadToken] = useState(0);

    const load = useCallback(() => {
        setLoading(true);
        vatReportingApi.getConfig(company, country)
            .then(setConfig)
            .catch(err => onError(err instanceof Error ? err.message : String(err)))
            .finally(() => setLoading(false));
    }, [company, country, onError]);

    useEffect(load, [load]);

    const contractOf = useCallback(
        (resource: ConfigResource): ConfigResourceContract | undefined =>
            config?.contracts.find(c => c.resource === resource),
        [config]);

    const section = useMemo(() => SECTIONS.find(s => s.key === active)!, [active]);
    const contract = contractOf(active);
    const rows = config?.configRows?.[active] ?? [];

    // A column whose field the contract does not have would render as a silent empty column;
    // dropping it makes a contract change visible as a missing column instead.
    const columns = useMemo(() => {
        if (!contract) return [];
        const known = new Set(contract.fields.map(f => f.name));
        return section.columns.filter(c => known.has(c.name));
    }, [contract, section]);

    /** A fresh row: key fields empty, everything else at the contract's own default. */
    const emptyRow = useCallback((resource: ConfigResource): ConfigRow => {
        const c = contractOf(resource);
        const row: ConfigRow = { companyCode: company };
        if (!c) return row;
        if (c.countryInKey) row.countryCode = country;
        for (const f of c.fields) {
            if (f.name === 'companyCode' || (c.countryInKey && f.name === 'countryCode')) continue;
            row[f.name] = f.kind === 'bool' ? false : f.nullable ? null : '';
        }
        return row;
    }, [contractOf, company, country]);

    const afterWrite = (message: string) => {
        setDialog(null);
        setConfirmDelete(null);
        setNotice(message);
        setReloadToken(x => x + 1);
        load();
    };

    const doDelete = async () => {
        if (!confirmDelete) return;
        const c = contractOf(confirmDelete.resource);
        if (!c) return;
        setDeleting(true);
        try {
            const key: ConfigRow = {};
            for (const f of c.fields) if (f.key) key[f.name] = confirmDelete.row[f.name] ?? '';
            await vatReportingApi.deleteConfigRow(confirmDelete.resource, key);
            afterWrite(t('config.deleted'));
        } catch (err) {
            onError(err instanceof Error ? err.message : String(err));
            setConfirmDelete(null);
        } finally {
            setDeleting(false);
        }
    };

    if (loading && !config) {
        return (
            <div className="p-12 text-center text-gray-500">
                <Loader2 className="h-5 w-5 animate-spin inline mr-2" />{t('page.loading')}
            </div>
        );
    }
    if (!config || !contract) {
        return <div className="p-8 text-center text-gray-500">{t('config.loadFailed')}</div>;
    }

    const pendingCount = (config.configRows['line-map'] ?? [])
        .filter(r => r.disposition === 'pending').length;

    return (
        <div className="space-y-4">
            {/* Retroactivity, said once and at the top rather than only in the save dialog. */}
            <div className="flex items-start gap-2 border border-blue-200 bg-blue-50 rounded p-3 text-sm text-blue-900">
                <Info className="h-4 w-4 mt-0.5 shrink-0" />
                <div>
                    <p>{t('config.retroWarning')}</p>
                    {pendingCount > 0 && (
                        <p className="mt-1">{t('config.pendingHint', { count: pendingCount })}</p>
                    )}
                </div>
            </div>

            {notice && (
                <div className="flex items-start justify-between gap-3 border border-green-200 bg-green-50 rounded p-3 text-sm text-green-900">
                    <span>{notice}</span>
                    <button onClick={() => setNotice(null)} className="text-green-600 hover:text-green-800 shrink-0">
                        <X className="h-4 w-4" />
                    </button>
                </div>
            )}

            {/* The section switcher IS the registry, rendered. */}
            <div className="flex flex-wrap gap-1.5">
                {SECTIONS.map(s => {
                    const count = (config.configRows[s.key] ?? []).length;
                    return (
                        <button key={s.key} onClick={() => setActive(s.key)}
                                className={`px-3 py-1.5 text-sm rounded border transition-colors ${
                                    active === s.key
                                        ? 'bg-blue-600 border-blue-600 text-white'
                                        : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                                }`}>
                            {t(`config.section.${s.key}.title`)}
                            <span className={`ml-1.5 text-xs ${active === s.key ? 'text-blue-100' : 'text-gray-400'}`}>
                                {count}
                            </span>
                        </button>
                    );
                })}
            </div>

            <div>
                <h3 className="text-sm font-semibold text-gray-900">
                    {t(`config.section.${active}.title`)}
                </h3>
                <p className="text-sm text-gray-500 mt-0.5">
                    {t(`config.section.${active}.description`)}
                    <span className="ml-1.5 text-xs text-gray-400 font-mono">
                        [Reporting].[{contract.table}]
                    </span>
                </p>
            </div>

            {section.warningKey && (
                <div className="flex items-start gap-2 border border-amber-200 bg-amber-50 rounded p-3 text-sm text-amber-900">
                    <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                    <span>{t(section.warningKey)}</span>
                </div>
            )}

            <ConfigTable
                contract={contract}
                columns={columns}
                rows={rows}
                widthStorageKey={`vat-config-${active}-col-widths`}
                canEdit={canEdit}
                onCreate={() => setDialog({ mode: 'create', resource: active, initial: emptyRow(active) })}
                onEdit={row => setDialog({ mode: 'edit', resource: active, initial: { ...row } })}
                // Duplicate opens a CREATE with the values kept and the key cleared - the only
                // honest way to offer "move this rule to another combination", because these
                // keys are the identity of the row and an update never moves one.
                onDuplicate={row => {
                    const cleared: ConfigRow = { ...row };
                    for (const f of contract.fields) {
                        if (f.key && f.name !== 'companyCode' && f.name !== 'countryCode') {
                            cleared[f.name] = '';
                        }
                    }
                    setDialog({ mode: 'create', resource: active, initial: cleared });
                }}
                onDelete={row => setConfirmDelete({ resource: active, row })}
                rowClass={section.rowClass}
            />

            <ConfigAuditPanel
                company={company}
                country={country}
                resource={active}
                contract={contract}
                reloadToken={reloadToken}
                onError={onError}
            />

            {dialog && (
                <ConfigRowDialog
                    resource={dialog.resource}
                    contract={contractOf(dialog.resource)!}
                    mode={dialog.mode}
                    initial={dialog.initial}
                    company={company}
                    country={country}
                    kennzahlOptions={config.kennzahlOptions}
                    order={SECTIONS.find(s => s.key === dialog.resource)?.formOrder}
                    onClose={() => setDialog(null)}
                    onSaved={() => afterWrite(
                        dialog.mode === 'create' ? t('config.created') : t('config.updated'))}
                />
            )}

            {confirmDelete && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
                    <div className="bg-white rounded shadow-xl w-full max-w-lg">
                        <div className="px-5 py-3 border-b">
                            <h3 className="font-semibold text-gray-900">{t('config.deleteTitle')}</h3>
                        </div>
                        <div className="px-5 py-4 space-y-2 text-sm">
                            <p className="text-gray-700">{t('config.deleteBody')}</p>
                            <p className="font-mono text-xs bg-gray-50 border rounded p-2 break-all">
                                {configRowKey(contractOf(confirmDelete.resource)!, confirmDelete.row)}
                            </p>
                            <p className="text-amber-800">{t('config.deleteWarning')}</p>
                        </div>
                        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t bg-gray-50">
                            <button onClick={() => setConfirmDelete(null)} disabled={deleting}
                                    className="px-3 py-1.5 text-sm rounded border bg-white hover:bg-gray-100">
                                {t('config.cancel')}
                            </button>
                            <button onClick={doDelete} disabled={deleting}
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-50">
                                {deleting && <Loader2 className="h-4 w-4 animate-spin" />}
                                {t('config.delete')}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
